import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, qs } from "../api";
import { Alert, Badge, Button, Card, Chips, DataTable, Empty, ErrorBox, FormError, Loading, Modal, Money, PageHeader, Reg, SelectInput, TextArea, useToast, useUnsavedWarning, AFTER_SAVE } from "../ui";
import { CustomerPicker, CustomerVehicles, type CustomerLite, type VehicleLite, DeleteRecordButton } from "../components";
import { DeptBadge, QuoteStatusBadge, date, dateTime, poundsInput, qtyLabel } from "../format";
import { lineNet, parsePounds, parseQty, formatGBP } from "../../shared/money";
import { ShareLinkButton } from "../extras";

export interface EditLine { kind: "labour" | "part" | "other"; description: string; qty: string; unitPrice: string }
export const blankLine = (): EditLine => ({ kind: "labour", description: "", qty: "1", unitPrice: "" });

export function LineEditor({ lines, onChange }: { lines: EditLine[]; onChange: (l: EditLine[]) => void }) {
  const set = (i: number, patch: Partial<EditLine>) => onChange(lines.map((l, k) => (k === i ? { ...l, ...patch } : l)));
  const total = lines.reduce((a, l) => a + (parseQty(l.qty) && parsePounds(l.unitPrice) !== null ? lineNet(parseQty(l.qty)!, parsePounds(l.unitPrice)!) : 0), 0);
  return (
    <div>
      <div className="table-wrap">
        <table className="table">
          <thead><tr><th style={{ width: 120 }}>Type</th><th>Description</th><th style={{ width: 90 }} className="num">Qty</th><th style={{ width: 130 }} className="num">Unit price £</th><th style={{ width: 110 }} className="num">Line</th><th style={{ width: 50 }} /></tr></thead>
          <tbody>
            {lines.map((l, i) => {
              const q = parseQty(l.qty); const p = parsePounds(l.unitPrice);
              return (
                <tr key={i}>
                  <td><select className="input" aria-label="Type" value={l.kind} onChange={(e) => set(i, { kind: e.target.value as EditLine["kind"] })}><option value="labour">Labour</option><option value="part">Part</option><option value="other">Other</option></select></td>
                  <td><input className="input" aria-label="Description" value={l.description} onChange={(e) => set(i, { description: e.target.value })} placeholder={l.kind === "labour" ? "e.g. Replace front brake pads" : "e.g. Brake pad set"} /></td>
                  <td><input className={`input right ${q === null ? "invalid" : ""}`} aria-label="Quantity" inputMode="decimal" value={l.qty} onChange={(e) => set(i, { qty: e.target.value })} /></td>
                  <td><input className={`input right ${l.unitPrice && p === null ? "invalid" : ""}`} aria-label="Unit price" inputMode="decimal" value={l.unitPrice} onChange={(e) => set(i, { unitPrice: e.target.value })} placeholder="0.00" /></td>
                  <td className="num">{q !== null && p !== null ? formatGBP(lineNet(q, p)) : "—"}</td>
                  <td><Button size="sm" variant="ghost" aria-label="Remove line" disabled={lines.length === 1} onClick={() => onChange(lines.filter((_, k) => k !== i))}>✕</Button></td>
                </tr>
              );
            })}
          </tbody>
          <tfoot><tr><td colSpan={4} className="right">Total (excl. any VAT)</td><td className="num">{formatGBP(total)}</td><td /></tr></tfoot>
        </table>
      </div>
      <Button size="sm" onClick={() => onChange([...lines, blankLine()])} style={{ marginTop: 8 }}>+ Add line</Button>
    </div>
  );
}

export const linesValid = (lines: EditLine[]) => lines.length > 0 && lines.every((l) => l.description.trim() && parseQty(l.qty) !== null && parsePounds(l.unitPrice) !== null);

export function QuoteList() {
  const [params, setParams] = useSearchParams();
  const status = params.get("status") ?? "";
  const list = useQuery({ queryKey: ["quotes", status], queryFn: () => api.get(`/quotes${qs({ status })}`) });
  return (
    <>
      <PageHeader title="Quotes" actions={<Link className="btn btn-primary" to="/quotes/new">+ New quote</Link>} />
      <Chips value={status} onChange={(v) => setParams(v ? { status: v } : {}, { replace: true })} options={[{ value: "", label: "All" }, { value: "draft", label: "Draft" }, { value: "sent", label: "Awaiting reply" }, { value: "accepted", label: "Accepted" }, { value: "declined", label: "Declined" }]} />
      <Card pad={false}>
        {list.isLoading ? <Loading /> : list.isError ? <ErrorBox error={list.error} retry={() => list.refetch()} /> : (
          <DataTable rows={list.data.quotes} rowLink={(x: any) => `/quotes/${x.id}`} empty={<Empty title="No quotes" />} columns={[
            { key: "n", label: "Quote", render: (x: any) => <><strong>{x.number}</strong> {x.current_revision > 1 && <span className="small muted">rev {x.current_revision}</span>}</> },
            { key: "c", label: "Customer", render: (x: any) => x.customer_name },
            { key: "r", label: "Vehicle", render: (x: any) => <Reg reg={x.registration} /> },
            { key: "d", label: "Dept", render: (x: any) => <DeptBadge d={x.department} /> },
            { key: "k", label: "Type", render: (x: any) => (x.kind === "additional" ? <Badge>Additional work</Badge> : "Quote") },
            { key: "t", label: "Total", num: true, render: (x: any) => <Money pence={x.total_pence} /> },
            { key: "s", label: "Status", render: (x: any) => <QuoteStatusBadge s={x.status} /> },
            { key: "u", label: "Updated", render: (x: any) => date(x.updated_at.slice(0, 10)) },
          ]} />
        )}
      </Card>
    </>
  );
}

export function QuoteNew() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const jobId = params.get("job");
  const job = useQuery({ queryKey: ["job", jobId], queryFn: () => api.get(`/jobs/${jobId}`), enabled: !!jobId });
  const [customer, setCustomer] = useState<CustomerLite | null>(null);
  const [vehicle, setVehicle] = useState<VehicleLite | null>(null);
  const [department, setDepartment] = useState(params.get("department") ?? "mechanical");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState<EditLine[]>([blankLine()]);
  useEffect(() => {
    const cid = params.get("customer");
    if (cid) api.get(`/customers/${cid}`).then((d) => setCustomer({ id: cid, name: d.customer.name, phone: d.customer.phone })).catch(() => {});
    const vid = params.get("vehicle");
    if (vid) api.get(`/vehicles/${vid}`).then((d) => setVehicle(d.vehicle)).catch(() => {});
  }, [params]);
  // From a job, only the job is sent: the server takes customer, vehicle and department from it.
  const m = useMutation({
    mutationFn: async (approvedNow: boolean) => {
      const r = await api.post<{ id: string }>("/quotes", jobId ? { jobId, notes, lines } : { customerId: customer!.id, vehicleId: vehicle?.id ?? null, enquiryId: params.get("enquiry"), department, notes, lines });
      if (approvedNow) await api.post(`/quotes/${r.id}/accept`, { revisionNo: 1, note: "Approved by the customer in person" });
      return { ...r, approvedNow };
    },
    onSuccess: (r) => navigate(r.approvedNow && jobId ? `/jobs/${jobId}` : `/quotes/${r.id}`, AFTER_SAVE),
  });
  useUnsavedWarning(lines.some((l) => l.description) && !m.isSuccess);
  return (
    <>
      <PageHeader title={jobId ? `Add price for job ${job.data?.job?.number ?? ""}` : "New quote"} back={jobId ? { to: `/jobs/${jobId}`, label: `job ${job.data?.job?.number ?? ""}`.trim() } : { to: "/quotes", label: "Quotes" }}
        sub={jobId ? "When the customer approves, the work is added to this same job — no new job card or booking." : "Quotes are not sales — nothing is recorded in the accounts until an invoice is issued."} />
      <div className="stack">
        {jobId ? (
          <Card title="Job">
            {job.isLoading ? <Loading /> : job.isError ? <ErrorBox error={job.error} /> : (
              <dl className="kv">
                <dt>Job</dt><dd><strong>{job.data.job.number}</strong> <DeptBadge d={job.data.job.department} /></dd>
                <dt>Customer</dt><dd>{job.data.job.customer_name}</dd>
                <dt>Vehicle</dt><dd>{job.data.job.registration ? <><Reg reg={job.data.job.registration} /> {[job.data.job.make, job.data.job.model].filter(Boolean).join(" ")}</> : "—"}</dd>
              </dl>
            )}
          </Card>
        ) : (
        <Card title="Customer">
          <div className="form-grid">
            <CustomerPicker value={customer} onChange={(c) => { setCustomer(c); setVehicle(null); }} />
            <SelectInput label="Department" value={department} onChange={(e) => setDepartment(e.target.value)} options={[{ value: "mechanical", label: "Mechanical" }, { value: "bodywork", label: "Bodywork" }]} />
            {customer && <div className="full">{vehicle ? <div className="row"><Reg reg={vehicle.registration} /> {vehicle.make} {vehicle.model}<Button size="sm" variant="ghost" onClick={() => setVehicle(null)}>Change</Button></div> : <CustomerVehicles customerId={customer.id} onPick={setVehicle} />}</div>}
          </div>
        </Card>
        )}
        <Card title="Items"><LineEditor lines={lines} onChange={setLines} /></Card>
        <Card title="Notes for the customer"><TextArea label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} /></Card>
        <div className="form-actions">
          <Button variant={jobId ? "secondary" : "primary"} disabled={(!jobId && !customer) || !linesValid(lines)} loading={m.isPending && !m.variables} onClick={() => m.mutate(false)}>{jobId ? "Save — waiting for approval" : "Save quote"}</Button>
          {jobId && <Button variant="primary" disabled={!linesValid(lines)} loading={m.isPending && !!m.variables} onClick={() => m.mutate(true)}>Save — customer has approved</Button>}
        </div>
        <FormError error={m.error} />
      </div>
    </>
  );
}

export function QuoteDetail() {
  const { id } = useParams();
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ["quote", id], queryFn: () => api.get(`/quotes/${id}`) });
  const [editing, setEditing] = useState<null | "draft" | "revise">(null);
  const [modal, setModal] = useState<null | "accept" | "decline">(null);
  const refresh = () => { qc.invalidateQueries({ queryKey: ["quote", id] }); qc.invalidateQueries({ queryKey: ["quotes"] }); qc.invalidateQueries({ queryKey: ["job"] }); };
  const navigate = useNavigate();
  const link = useMutation({ mutationFn: (jobId: string) => api.post(`/quotes/${id}/link-job`, { jobId }), onSuccess: (r: any) => { refresh(); toast("success", "Quote added to the open job"); navigate(`/jobs/${r.jobId}`); }, onError: (e) => toast("error", (e as Error).message) });
  const start = useMutation({ mutationFn: () => api.post<{ jobId: string }>(`/quotes/${id}/start-job`, {}), onSuccess: (r) => { refresh(); toast("success", "Job started"); navigate(`/jobs/${r.jobId}`); }, onError: (e) => toast("error", (e as Error).message) });
  const send = useMutation({ mutationFn: () => api.post(`/quotes/${id}/send`), onSuccess: () => { refresh(); toast("success", "Marked as sent — revision locked"); }, onError: (e) => toast("error", (e as Error).message) });
  if (q.isLoading) return <Loading />;
  if (q.isError) return <ErrorBox error={q.error} retry={() => q.refetch()} />;
  const { quote: x, revisions, openJobs } = q.data;
  const current = revisions.find((r: any) => r.revision_no === x.current_revision);
  const open = ["draft", "sent"].includes(x.status);
  return (
    <>
      <PageHeader back={{ to: "/quotes", label: "Quotes" }} title={<>{x.number} <QuoteStatusBadge s={x.status} /></>}
        sub={<><Link to={`/customers/${x.customer_id}`}>{x.customer_name}</Link> · {x.registration && <Reg reg={x.registration} />} <DeptBadge d={x.department} /> {x.job_number && <>· for job <Link to={`/jobs/${x.job_id}`}>{x.job_number}</Link></>}</>}
        actions={<>
          {x.status === "draft" && !current.locked && <Button onClick={() => setEditing("draft")}>Edit draft</Button>}
          {open && current.locked ? <Button onClick={() => setEditing("revise")}>New revision</Button> : null}
          {x.status === "draft" && <Button loading={send.isPending} onClick={() => send.mutate()}>Mark as sent</Button>}
          {open && <ShareLinkButton purpose="quote_approval" quoteId={x.id} customerName={x.customer_name} label="Customer approval link" />}
          <DeleteRecordButton kind="quote" id={x.id} onDone={() => navigate("/quotes")} />
          {open && <Button variant="danger" onClick={() => setModal("decline")}>Declined</Button>}
          {open && <Button variant="primary" onClick={() => setModal("accept")}>Record acceptance</Button>}
          {!x.job_id && openJobs.map((o: any) => <Button key={o.id} variant="primary" loading={link.isPending} onClick={() => link.mutate(o.id)}>Add to open job {o.number}</Button>)}
          {x.status === "accepted" && !x.job_id && !openJobs.length && <Button variant="primary" loading={start.isPending} onClick={() => start.mutate()}>Start job now (walk-in)</Button>}
          {x.status === "accepted" && !x.job_id && <Link className="btn btn-secondary" to={`/bookings?new=1&quote=${x.id}&customer=${x.customer_id}&vehicle=${x.vehicle_id ?? ""}&department=${x.department}`}>Book for later</Link>}
          {x.job_id && <Link className="btn btn-secondary" to={`/jobs/${x.job_id}`}>Open job {x.job_number}</Link>}
        </>} />
      {x.status === "accepted" && <Alert kind="success" title={`Revision ${revisions.find((r: any) => r.id === x.accepted_revision_id)?.revision_no} accepted`}>{dateTime(x.accepted_at)} via {x.accepted_via}{x.acceptance_note ? ` — ${x.acceptance_note}` : ""}</Alert>}
      {editing ? <ReviseEditor quoteId={x.id} mode={editing} initial={current} onDone={() => { setEditing(null); refresh(); }} /> : (
        <div className="stack" style={{ marginTop: 12 }}>
          {revisions.map((r: any) => (
            <Card key={r.id} title={<>Revision {r.revision_no} {r.id === x.accepted_revision_id && <Badge tone="green">Accepted version</Badge>} {r.locked ? <Badge>Locked</Badge> : <Badge tone="amber">Draft</Badge>}</>}
              actions={<span className="small muted">{dateTime(r.created_at)}</span>} pad={false}>
              <DataTable rows={r.lines} columns={[
                { key: "k", label: "Type", render: (l: any) => l.kind },
                { key: "d", label: "Description", render: (l: any) => l.description },
                { key: "q", label: "Qty", num: true, render: (l: any) => qtyLabel(l.qty_milli) },
                { key: "u", label: "Unit", num: true, render: (l: any) => <Money pence={l.unit_price_pence} /> },
                { key: "t", label: "Line", num: true, render: (l: any) => <Money pence={l.line_total_pence} /> },
              ]} footer={<>
                {r.vat_registered ? <><tr><td colSpan={4} className="right">Net</td><td className="num"><Money pence={r.subtotal_pence} /></td></tr><tr><td colSpan={4} className="right">VAT</td><td className="num"><Money pence={r.vat_pence} /></td></tr></> : null}
                <tr><td colSpan={4} className="right">Total</td><td className="num"><Money pence={r.total_pence} /></td></tr>
              </>} />
              {r.notes && <div className="card-body small" style={{ whiteSpace: "pre-wrap" }}>{r.notes}</div>}
            </Card>
          ))}
        </div>
      )}
      {modal === "accept" && <AcceptModal quote={x} revisionNo={x.current_revision} onClose={() => setModal(null)} onDone={refresh} />}
      {modal === "decline" && <DeclineModal quoteId={x.id} onClose={() => setModal(null)} onDone={refresh} />}
    </>
  );
}

function ReviseEditor({ quoteId, mode, initial, onDone }: { quoteId: string; mode: "draft" | "revise"; initial: any; onDone: () => void }) {
  const [start] = useState(() => ({ lines: initial.lines.map((l: any): EditLine => ({ kind: l.kind, description: l.description, qty: qtyLabel(l.qty_milli), unitPrice: poundsInput(l.unit_price_pence) })), notes: initial.notes ?? "" }));
  const [lines, setLines] = useState<EditLine[]>(start.lines);
  const [notes, setNotes] = useState(start.notes);
  const toast = useToast();
  useUnsavedWarning(JSON.stringify({ lines, notes }) !== JSON.stringify(start));
  const m = useMutation({
    mutationFn: () => mode === "draft" ? api.put(`/quotes/${quoteId}/draft`, { lines, notes }) : api.post(`/quotes/${quoteId}/revise`, { lines, notes }),
    onSuccess: () => { toast("success", mode === "draft" ? "Draft saved" : "New revision created"); onDone(); },
  });
  return (
    <div className="stack" style={{ marginTop: 12 }}>
      {mode === "revise" && <Alert kind="info">The sent revision stays on record unchanged. This creates revision {initial.revision_no + 1}.</Alert>}
      <Card title={mode === "draft" ? `Edit revision ${initial.revision_no}` : `Revision ${initial.revision_no + 1}`}><LineEditor lines={lines} onChange={setLines} /></Card>
      <Card title="Notes"><TextArea label="Notes for the customer" value={notes} onChange={(e) => setNotes(e.target.value)} /></Card>
      <div className="form-actions"><Button onClick={onDone}>Cancel</Button><Button variant="primary" disabled={!linesValid(lines)} loading={m.isPending} onClick={() => m.mutate()}>Save</Button></div>
      <FormError error={m.error} />
    </div>
  );
}

function AcceptModal({ quote, revisionNo, onClose, onDone }: { quote: any; revisionNo: number; onClose: () => void; onDone: () => void }) {
  const [note, setNote] = useState("");
  const toast = useToast();
  const m = useMutation({ mutationFn: () => api.post(`/quotes/${quote.id}/accept`, { revisionNo, note: note || null }), onSuccess: () => { toast("success", "Acceptance recorded"); onDone(); onClose(); } });
  return (
    <Modal title={`Record acceptance of revision ${revisionNo}`} onClose={onClose} footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" loading={m.isPending} onClick={() => m.mutate()}>Confirm acceptance</Button></>}>
      <p>Only confirm if the customer has agreed to <strong>revision {revisionNo}</strong> — the exact version will be stored as the accepted one.</p>
      {quote.job_id && <Alert kind="info">The accepted lines will be added to job {quote.job_number} as approved work.</Alert>}
      <div style={{ marginTop: 12 }}><TextArea label="How was it accepted?" placeholder="e.g. Customer approved by phone at 2:15pm" value={note} onChange={(e) => setNote(e.target.value)} /></div>
      <FormError error={m.error} />
    </Modal>
  );
}

function DeclineModal({ quoteId, onClose, onDone }: { quoteId: string; onClose: () => void; onDone: () => void }) {
  const [note, setNote] = useState("");
  const m = useMutation({ mutationFn: () => api.post(`/quotes/${quoteId}/decline`, { note: note || null }), onSuccess: () => { onDone(); onClose(); } });
  return (
    <Modal title="Mark quote as declined" onClose={onClose} footer={<><Button onClick={onClose}>Cancel</Button><Button variant="danger" loading={m.isPending} onClick={() => m.mutate()}>Mark declined</Button></>}>
      <TextArea label="Reason (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
      <FormError error={m.error} />
    </Modal>
  );
}
